import { defineStore } from 'pinia';

export interface PopupNotification {
    type: 'error' | 'success' | 'action'
    title: string
    permanent: boolean
    actionText?: string
    action?: () => void
}

interface State {
    popupNotifications: (PopupNotification & { id: string })[]
}

// Store resets must not reuse IDs while earlier expiry timers can still run.
let notificationIdSequence = 0;

export const usePopupNotificationsStore = defineStore('popupNotifications', {
    state: (): State => ({
        popupNotifications: [],
    }),
    getters: {
        getPopupNotifications: state => state.popupNotifications,
    },
    actions: {
        newPopupNotification(value: PopupNotification): void {
            if (this.isPopupDuplicate(value)) {
                return;
            }

            notificationIdSequence += 1;
            const id = notificationIdSequence.toString();
            this.popupNotifications.push({ ...value, id });

            if (!value.permanent) {
                setTimeout(() => this.removeNotificationById(id), 6000);
            }
        },
        removeNotificationById(id: string): void {
            this.popupNotifications = this.popupNotifications.filter(notification => notification.id !== id);
        },
        removeNotificationByTitle(title: string): void {
            this.popupNotifications = this.popupNotifications.filter(notification => notification.title !== title);
        },
        // If the most recent notification is the same as the new one and it is still visible, don't add it
        isPopupDuplicate(value: PopupNotification): boolean {
            if (this.popupNotifications.length === 0) {
                return false;
            }

            const lastNotification = this.popupNotifications[this.popupNotifications.length - 1];
            if (lastNotification === undefined) {
                return false;
            }

            if (lastNotification.title !== value.title) {
                return false;
            }

            return true;
        },
    },
});
